import { Fragment, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import CommonFields from "components/CommonFields";
import { BANK_NAMES, INPUT_TYPE } from "globalConstants";
import useBSAReport from "hooks/useBsaReport";
import convertCamelCaseToTitleCase from "utils/convertCamelCaseToTitleCase";
import generateTestId from "utils/generateTestId";
import fixture from "./fixture";

const LoanAccountAdditionalItem = ({
    index,
    prefix,
    setCurrentInput,
    pane,
}: {
    index: number;
    prefix: string;
    setCurrentInput: any;
    pane?: string;
}) => {
    const loan = useWatch({ name: `${prefix}.loanAccountAdditional.${index}` });
    const entity = useWatch({ name: `${prefix.replace(".document.business", "")}.entity` });
    let shouldUpload = true;
    let errorText = "";

    for (const key of ["accountNumber", "bankName"]) {
        if (!loan[key]) {
            errorText += ", " + convertCamelCaseToTitleCase(key);
            shouldUpload = false;
        }
    }

    for (const key of ["entityName", "pan"]) {
        if (key === "pan" && !entity[key]) {
            errorText += ", " + "PAN";
            shouldUpload = false;
        }
        if (!entity[key] && key === "entityName") {
            errorText += ", " + convertCamelCaseToTitleCase(key);
            shouldUpload = false;
        }
    }

    if (errorText) errorText += " required";
    const { setValue } = useFormContext();

    const incomeLoan = useWatch({ name: `applicant.income.loanOutstanding.${index}` });

    useEffect(() => {
        setValue(`${prefix}.loanAccountAdditional.${index}.bankName`, incomeLoan?.financedBy);
        setValue(`${prefix}.loanAccountAdditional.${index}.accountType`, incomeLoan?.accountType);
        setValue(`${prefix}.loanAccountAdditional.${index}.accountNumber`, incomeLoan?.accountNumber);
        if (incomeLoan?.financedBy !== "") {
            let bankCode = BANK_NAMES.find((bank) => bank.name === loan.bankName)?.code ?? "";
            setValue(`${prefix}.loanAccountAdditional.${index}.bankCode`, bankCode);
        }
    }, [incomeLoan]);

    return (
        <Fragment>
            {fixture.map(({ name, inputType, options, subtitle, label, editable, placeholder, showMask }, num) => {
                return (
                    <CommonFields
                        key={num}
                        inputType={inputType}
                        showMask={showMask}
                        data-testid={generateTestId(`${label}-loan-account-additional-${index + 1}-${pane}`, "field")}
                        name={`${prefix}.loanAccountAdditional.${index}.${name}`}
                        placeholder={placeholder}
                        subtitle={subtitle}
                        readOnly={localStorage.getItem("disable_form") === "true" || !editable}
                        options={
                            options?.map((value) => ({
                                value,
                                label: value,
                            })) || []
                        }
                        label={label}
                    />
                );
            })}
            <CommonFields
                inputType={INPUT_TYPE.FILE}
                label="Loan Account Statement"
                prop={{
                    value: "Entire Loan Period",
                }}
                readOnly={localStorage.getItem("disable_form") === "true" || !shouldUpload}
                name={`${prefix}.loanAccountAdditional.${index}.statement`}
                multiple={true}
                handleChange={useBSAReport({
                    name: "statement",
                    fileAddress: `${prefix}.loanAccountAdditional.${index}.statement`,
                    multiple: true,
                    prefix,
                })}
                onClick={() => {
                    if (!shouldUpload) {
                        toast.dismiss();
                        toast.error(errorText?.slice(2));
                    }
                }}
                value={loan?.statement}
                showCross={localStorage.getItem("disable_form") !== "true" && loan?.statement && loan?.statement?.[0]?.link !== ""}
                handleFileModalOpen={({ position }) =>
                    setCurrentInput({ name: `${prefix}.loanAccountAdditional.${index}.statement`, open: true, multiple: true, position: position })
                }
            />
        </Fragment>
    );
};

export default LoanAccountAdditionalItem;
